// File: StepOptions - data-driven step renderer
// Created: 2026-05-12
// Updated: 2026-05-29
// Author: Erick Hernández Silva

import React from 'react';
import type { Step, SubOption } from './carWizardTypes';
import { fixedSubOptions } from './rules';

type VariantMode = 'matching' | 'fixed';

interface Props {
  step: Step;
  selectedOptionId?: string;
  selectedSubOptionId?: string;
  variantMode: VariantMode;
  linkedStepName?: string;
  hiddenOptionIds: Set<string>;
  disabledOptionIds: Set<string>;
  disabled?: boolean;
  onSelectOption: (optionId: string) => void;
  onSelectSubOption: (subOptionId: string) => void;
  onVariantMode: (mode: VariantMode, fixedSubId?: string) => void;
  headerText?: string;
  // Variant locked by a forceSubOption rule (e.g. Contrast package → black steering).
  forcedSubOptionId?: string | null;
}

const CHECK_ICON = 'https://cdn.prod.website-files.com/68472be536d16d9004a489e5/69c2f77e872fffc7542078c5_IcOutlineCheck.png';

const StepOptions: React.FC<Props> = ({
  step,
  selectedOptionId,
  selectedSubOptionId,
  variantMode,
  linkedStepName,
  hiddenOptionIds,
  disabledOptionIds,
  disabled = false,
  onSelectOption,
  onSelectSubOption,
  onVariantMode,
  headerText = 'SELECT OPTION',
  forcedSubOptionId = null,
}) => {
  const visibleOptions = step.options.filter((o) => !hiddenOptionIds.has(o.optionId));
  const selectedOption = step.options.find((o) => o.optionId === selectedOptionId) || null;

  const allOptionsHaveNoImages = visibleOptions.every((opt) => {
    const presentationOk = !!opt.presentationImage && opt.presentationImage.startsWith('http');
    const anyViewOk = Object.values(opt.images || {}).some((v) => typeof v === 'string' && v.startsWith('http'));
    return !presentationOk && !anyViewOk;
  });
  const shouldShowAsTextOnly = step.isTextOnly || allOptionsHaveNoImages;
  const showOptionDescriptions = visibleOptions.some((o) => !!o.optionDescription);

  // Coupled sub-options (e.g. steering "Original") with at least one fixed
  // variant get a manual selector: "Match <linkedStep>" vs each fixed variant.
  const isCoupled = !!selectedOption?.subOptionsLinkedToStepId;
  const fixedSubs = isCoupled ? fixedSubOptions(selectedOption) : [];
  const forcedSub = forcedSubOptionId
    ? selectedOption?.subOptions?.find((s) => s.optionId === forcedSubOptionId) || null
    : null;
  const showVariantSelector = isCoupled && fixedSubs.length > 0 && !forcedSub;

  // Plain (non-coupled) sub-options get the regular variant grid.
  const plainSubOptions: SubOption[] =
    selectedOption && !isCoupled && !forcedSub ? selectedOption.subOptions || [] : [];

  const priceLabel = (price: number) =>
    `${price > 0 ? '+' : ''}${price.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })}`;

  return (
    <>
      <div className="mb-4">
        <h3 className="text-sm font-bold tracking-wider text-gray-800 mb-3">{headerText}</h3>

        <div className={`grid gap-y-3.5 gap-x-2 ${shouldShowAsTextOnly ? 'grid-cols-1' : 'grid-cols-2'}`}>
          {visibleOptions.map((option) => {
            const isSelected = selectedOptionId === option.optionId;
            const optDisabled = disabled || disabledOptionIds.has(option.optionId);
            const previewImage = option.presentationImage && option.presentationImage.startsWith('http')
              ? option.presentationImage
              : option.images.view1;

            return (
              <label
                key={option.optionId}
                className={`cursor-pointer transition-all duration-200 ${
                  shouldShowAsTextOnly ? 'flex items-start gap-3 p-3 border-b border-gray-200 last:border-b-0' : ''
                }`}
              >
                {shouldShowAsTextOnly && (
                  <input
                    type="radio"
                    name={`step-${step.stepId}`}
                    checked={isSelected}
                    disabled={optDisabled}
                    onChange={() => !optDisabled && onSelectOption(option.optionId)}
                    className="mt-1 custom-radio"
                  />
                )}
                <div
                  onClick={() => !optDisabled && !shouldShowAsTextOnly && onSelectOption(option.optionId)}
                  className={`flex-1 ${optDisabled ? 'cursor-not-allowed opacity-60' : ''}`}
                >
                  {!shouldShowAsTextOnly ? (
                    previewImage && previewImage.startsWith('http') ? (
                      <div className="relative overflow-hidden transition-all duration-200 hover:shadow-lg aspect-video">
                        <img src={previewImage} alt={option.optionName} className="w-full h-full object-contain" />
                        {isSelected && (
                          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                            <img src={CHECK_ICON} alt="Selected" className="w-4 h-4" style={{ filter: 'invert(1)' }} />
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="relative overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200 aspect-video flex flex-col items-center justify-center p-4">
                        <svg className="w-12 h-12 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                        </svg>
                        <p className="text-xs text-gray-500 text-center">No image available</p>
                        {isSelected && (
                          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                            <img src={CHECK_ICON} alt="Selected" className="w-4 h-4" style={{ filter: 'invert(1)' }} />
                          </div>
                        )}
                      </div>
                    )
                  ) : null}

                  <div className={shouldShowAsTextOnly ? '' : 'mt-2'}>
                    <div className="text-sm font-medium text-gray-900">{option.optionName}</div>
                    {showOptionDescriptions && option.optionDescription && (
                      <div className="text-sm font-normal text-gray-500 mt-1">{option.optionDescription}</div>
                    )}
                    {option.optionPrice !== 0 && (
                      <div className="text-sm font-normal text-gray-600 mt-1">{priceLabel(option.optionPrice)}</div>
                    )}
                  </div>
                </div>
              </label>
            );
          })}
        </div>
      </div>

      {/* Variant locked by a rule */}
      {forcedSub && (
        <div className="mb-6 pl-1">
          <div className="text-xs font-bold tracking-wider text-gray-600 mb-2">VARIANT</div>
          <label className="flex items-start gap-3 py-1 cursor-not-allowed">
            <input type="radio" checked readOnly disabled className="mt-1 custom-radio" />
            <div>
              <div className="text-sm font-medium text-gray-800">{forcedSub.optionName}</div>
              <div className="text-xs text-gray-500">Set by your current selection</div>
            </div>
          </label>
        </div>
      )}

      {/* Coupled variant selector (matching vs fixed) */}
      {showVariantSelector && (
        <div className="mb-6 pl-1">
          <div className="text-xs font-bold tracking-wider text-gray-600 mb-2">SELECT VARIANT</div>
          <div className="space-y-2">
            <label className={`flex items-start gap-3 py-1 ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}>
              <input
                type="radio"
                name={`variant-${step.stepId}`}
                checked={variantMode === 'matching'}
                disabled={disabled}
                onChange={() => !disabled && onVariantMode('matching')}
                className="mt-1 custom-radio"
              />
              <div className="text-sm font-medium text-gray-800">
                {linkedStepName ? `Match ${linkedStepName.toLowerCase()}` : 'Match interior color'}
              </div>
            </label>

            {fixedSubs.map((sub) => {
              const isSel = variantMode === 'fixed' && selectedSubOptionId === sub.optionId;
              return (
                <label key={sub.optionId} className={`flex items-start gap-3 py-1 ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}>
                  <input
                    type="radio"
                    name={`variant-${step.stepId}`}
                    checked={isSel}
                    disabled={disabled}
                    onChange={() => !disabled && onVariantMode('fixed', sub.optionId)}
                    className="mt-1 custom-radio"
                  />
                  <div className="text-sm font-medium text-gray-800">{sub.optionName}</div>
                </label>
              );
            })}
          </div>
        </div>
      )}

      {/* Plain variant grid (non-coupled sub-options) */}
      {plainSubOptions.length > 0 && (
        <div className="mb-6">
          <h3 className="text-xs font-bold tracking-wider text-gray-600 mb-4">SELECT VARIANT</h3>
          <div className="grid grid-cols-2 gap-3">
            {plainSubOptions.map((subOption) => {
              const isSelected = selectedSubOptionId === subOption.optionId;
              return (
                <div
                  key={subOption.optionId}
                  onClick={() => !disabled && onSelectSubOption(subOption.optionId)}
                  className={`transform transition-transform duration-200 hover:scale-105 ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                >
                  {subOption.images.view1 && subOption.images.view1.startsWith('http') ? (
                    <div className="relative rounded-lg overflow-hidden transition-all duration-200 hover:shadow-lg">
                      <img src={subOption.images.view1} alt={subOption.optionName} className="w-full h-auto object-contain" />
                      {isSelected && (
                        <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                          <img src={CHECK_ICON} alt="Selected" className="w-4 h-4" style={{ filter: 'invert(1)' }} />
                        </div>
                      )}
                    </div>
                  ) : null}
                  <div className="mt-2">
                    <div className="text-sm font-medium text-gray-800">{subOption.optionName}</div>
                    {subOption.optionPrice !== 0 && (
                      <div className="text-xs text-gray-500">{priceLabel(subOption.optionPrice)}</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
};

export default StepOptions;
